/**
 * The vendor's visualizer layout — where every piece of the shopper's visualizer
 * sits, how big the panels are, what each piece shows and which features exist.
 *
 * COPY of ai-visualizer `src/modules/Layout/layout.schema.ts` (with
 * layout.normalize.ts beside it). The visualizer renders the layout; this
 * editor writes it. The two must agree on every id, so change both together.
 *
 * A vendor without a layout gets `DEFAULT_LAYOUT`, which is the visualizer as it
 * was before layouts existed — nothing changes for anyone until they choose to.
 * Only the arrangement is configurable: every widget keeps its own behaviour
 * wherever it is placed.
 */

export const LAYOUT_VERSION = 1;

// ── Room view (desktop) ────────────────────────────────────────────────────

/** Places a widget can live in the room view. */
export const ROOM_ZONES = [
  'topbar.start',
  'topbar.center',
  'topbar.end',
  'panel.header',
  'canvas.topLeft',
  'canvas.topCenter',
  'canvas.topRight',
  'canvas.bottomLeft',
  'canvas.bottomCenter',
  'canvas.bottomRight',
] as const;
export type RoomZone = (typeof ROOM_ZONES)[number];

export const TOPBAR_ZONES: RoomZone[] = ['topbar.start', 'topbar.center', 'topbar.end'];
export const CANVAS_ZONES: RoomZone[] = [
  'canvas.topLeft', 'canvas.topCenter', 'canvas.topRight',
  'canvas.bottomLeft', 'canvas.bottomCenter', 'canvas.bottomRight',
];

/** The movable pieces of the room view. */
export const ROOM_WIDGETS = [
  'logo',
  'changeRoom',
  'appliedProducts',
  'productInfo',
  'share',
  'download',
  'compare',
  'upload',
  'order',
  'cart',
  'favorites',
  'addProduct',
  'menu',
  // The design assistant's way in. Shows only for vendors granted the assistant.
  'assistant',
  // Save this room (and reopen it, by email link, anywhere). Shows only where saved rooms are on.
  'saveRoom',
] as const;
export type RoomWidgetId = (typeof ROOM_WIDGETS)[number];

export type WidgetDisplay = 'icon-label' | 'icon' | 'label';

/** What the product-info widget can show, in the vendor's order. */
export const PRODUCT_INFO_FIELDS = ['brand', 'name', 'color', 'size', 'pattern', 'sku', 'type', 'collection'] as const;
export type ProductInfoField = (typeof PRODUCT_INFO_FIELDS)[number];

export interface WidgetPlacement {
  id: RoomWidgetId;
  zone: RoomZone;
  visible: boolean;
  /** Buttons: icon and label, icon only, or label only. */
  display?: WidgetDisplay;
  /** productInfo: which fields, in order. */
  fields?: ProductInfoField[];
}

export type BarStyle = 'attached' | 'floating' | 'glass';

export interface PanelLayout {
  side: 'left' | 'right';
  /** Width at ≥1200px wide; smaller screens take 100px off, very wide ones add 50. */
  width: number;
  style: 'attached' | 'floating';
  /** `auto`: collapsed until a product family is chosen (the original behaviour). */
  start: 'auto' | 'open';
  collapsible: boolean;
  tabs: { cart: boolean; favorites: boolean };
  catalogColumns: number;
}

export interface DesktopRoomLayout {
  widgets: WidgetPlacement[];
  panel: PanelLayout;
  topBar: { style: BarStyle };
  surfaceTools: { style: BarStyle };
  canvas: { background?: string };
}

// ── Room view (mobile) ─────────────────────────────────────────────────────

export const MOBILE_ACTIONS = ['share', 'order', 'compare', 'upload'] as const;
export type MobileActionId = (typeof MOBILE_ACTIONS)[number];

export interface MobileRoomLayout {
  /** The top bar's actions, in order. One missing from the list is not shown. */
  actions: MobileActionId[];
  /** How many sit in the bar before the rest go into its menu. */
  maxVisible: number;
  showChangeRoom: boolean;
}

// ── Gallery (room picker) ──────────────────────────────────────────────────

export interface GalleryLayout {
  header: {
    visible: boolean;
    /** split: logo left, title right (original). */
    align: 'split' | 'center' | 'left';
    showLogo: boolean;
    showSubtitle: boolean;
    /** Empty means the translated default. */
    title: string;
    subtitle: string;
  };
  arrangement: 'masonry' | 'grid' | 'carousel';
  columns: number;
  cardAspect: '4/3' | '1/1' | '3/4' | '16/9';
  showNames: boolean;
  filters: { visible: boolean };
  upload: { visible: boolean; position: 'first' | 'last'; qr: boolean };
  width: 'narrow' | 'normal' | 'wide';
  background?: string;
}

// ── Features ───────────────────────────────────────────────────────────────

/** Features that are not widgets of their own (those are hidden by hiding the widget). */
export const FEATURE_KEYS = [
  'search', 'filters', 'measurement', 'rotate', 'remove', 'variants', 'render', 'objectsPanel',
  // The smart assistant: recommendations, "this or that", estimates. It also
  // needs the catalogue's vision insights, and stays hidden without them.
  'assistant',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];
export type FeatureFlags = Record<FeatureKey, boolean>;

export interface VisualizerLayout {
  version: number;
  /** The preset it was last started from, for the editor. */
  preset?: string;
  gallery: GalleryLayout;
  room: { desktop: DesktopRoomLayout; mobile: MobileRoomLayout };
  features: FeatureFlags;
}

// ── Defaults: the visualizer before layouts ────────────────────────────────

const CLASSIC_WIDGETS: WidgetPlacement[] = [
  { id: 'logo', zone: 'panel.header', visible: true },
  { id: 'changeRoom', zone: 'panel.header', visible: true, display: 'icon' },
  { id: 'saveRoom', zone: 'panel.header', visible: true, display: 'icon' },
  { id: 'appliedProducts', zone: 'topbar.start', visible: true },
  { id: 'productInfo', zone: 'topbar.start', visible: true, fields: ['brand', 'name'] },
  { id: 'assistant', zone: 'topbar.end', visible: true, display: 'icon-label' },
  { id: 'share', zone: 'topbar.end', visible: true, display: 'icon-label' },
  { id: 'download', zone: 'topbar.end', visible: true, display: 'icon-label' },
  { id: 'compare', zone: 'topbar.end', visible: true, display: 'icon-label' },
  { id: 'upload', zone: 'topbar.end', visible: true, display: 'icon-label' },
  { id: 'order', zone: 'topbar.end', visible: true, display: 'icon-label' },
  { id: 'menu', zone: 'topbar.end', visible: true, display: 'icon-label' },
  { id: 'addProduct', zone: 'canvas.bottomLeft', visible: true, display: 'icon' },
  { id: 'cart', zone: 'topbar.end', visible: false, display: 'icon-label' },
  { id: 'favorites', zone: 'topbar.end', visible: false, display: 'icon-label' },
];

export const DEFAULT_LAYOUT: VisualizerLayout = {
  version: LAYOUT_VERSION,
  preset: 'classic',
  gallery: {
    header: { visible: true, align: 'split', showLogo: true, showSubtitle: true, title: '', subtitle: '' },
    arrangement: 'masonry',
    columns: 4,
    cardAspect: '4/3',
    showNames: false,
    filters: { visible: true },
    upload: { visible: true, position: 'first', qr: true },
    width: 'normal',
  },
  room: {
    desktop: {
      widgets: CLASSIC_WIDGETS,
      panel: {
        side: 'left',
        width: 420,
        style: 'attached',
        start: 'auto',
        collapsible: true,
        tabs: { cart: true, favorites: true },
        catalogColumns: 3,
      },
      topBar: { style: 'attached' },
      surfaceTools: { style: 'attached' },
      canvas: {},
    },
    mobile: { actions: ['share', 'order', 'compare', 'upload'], maxVisible: 2, showChangeRoom: true },
  },
  features: {
    search: true,
    filters: true,
    measurement: true,
    rotate: true,
    remove: true,
    variants: true,
    render: true,
    objectsPanel: true,
    assistant: true,
  },
};

// ── Presets ────────────────────────────────────────────────────────────────

type DeepPartial<T> = { [K in keyof T]?: T[K] extends Array<infer U> ? U[] : T[K] extends object ? DeepPartial<T[K]> : T[K] };

const w = (id: RoomWidgetId, zone: RoomZone, extra: Partial<WidgetPlacement> = {}): WidgetPlacement =>
  ({ id, zone, visible: true, ...extra });
const hidden = (id: RoomWidgetId, zone: RoomZone = 'topbar.end'): WidgetPlacement => ({ id, zone, visible: false });

/**
 * Starting points. A preset is only a layout: once applied, everything in it
 * can be changed, and the result is saved as the vendor's own.
 */
export const LAYOUT_PRESETS: Record<string, DeepPartial<VisualizerLayout>> = {
  classic: {},

  studio: {
    gallery: {
      header: { align: 'center' },
      arrangement: 'grid',
      columns: 3,
      cardAspect: '4/3',
      showNames: true,
      width: 'normal',
    },
    room: {
      desktop: {
        widgets: [
          w('logo', 'panel.header'),
          w('changeRoom', 'topbar.start', { display: 'icon-label' }),
          w('appliedProducts', 'topbar.center'),
          w('productInfo', 'topbar.center', { fields: ['name', 'size'] }),
          w('assistant', 'topbar.end', { display: 'icon-label' }),
          w('share', 'topbar.end', { display: 'icon' }),
          w('download', 'topbar.end', { display: 'icon' }),
          w('compare', 'topbar.end', { display: 'icon' }),
          w('upload', 'topbar.end', { display: 'icon' }),
          w('order', 'topbar.end', { display: 'icon-label' }),
          w('addProduct', 'canvas.bottomLeft', { display: 'icon-label' }),
          hidden('menu'),
          w('cart', 'panel.header', { display: 'icon' }),
          hidden('favorites'),
        ],
        panel: { side: 'right', width: 400, style: 'floating', start: 'auto', collapsible: true, tabs: { cart: true, favorites: true }, catalogColumns: 2 },
        topBar: { style: 'glass' },
        surfaceTools: { style: 'glass' },
        canvas: { background: '#ECE9E4' },
      },
    },
  },

  immersive: {
    gallery: {
      header: { align: 'left' },
      arrangement: 'carousel',
      columns: 3,
      cardAspect: '4/3',
      showNames: true,
      filters: { visible: false },
      upload: { position: 'last' },
      width: 'wide',
    },
    room: {
      desktop: {
        widgets: [
          w('logo', 'canvas.topLeft'),
          w('changeRoom', 'canvas.topLeft', { display: 'icon' }),
          w('assistant', 'canvas.topRight', { display: 'icon-label' }),
          w('share', 'canvas.topRight', { display: 'icon' }),
          w('download', 'canvas.topRight', { display: 'icon' }),
          w('upload', 'canvas.topRight', { display: 'icon' }),
          w('compare', 'canvas.topCenter', { display: 'icon-label' }),
          w('productInfo', 'canvas.bottomLeft', { fields: ['brand', 'name', 'size'] }),
          w('addProduct', 'canvas.bottomRight', { display: 'icon-label' }),
          w('order', 'canvas.bottomRight', { display: 'icon-label' }),
          hidden('appliedProducts', 'canvas.bottomLeft'),
          hidden('menu', 'canvas.topRight'),
          hidden('cart', 'canvas.topRight'),
          hidden('favorites', 'canvas.topRight'),
        ],
        panel: { side: 'right', width: 380, style: 'floating', start: 'auto', collapsible: true, tabs: { cart: true, favorites: false }, catalogColumns: 2 },
        topBar: { style: 'glass' },
        surfaceTools: { style: 'glass' },
        canvas: { background: '#16171A' },
      },
      mobile: { actions: ['order', 'share', 'compare', 'upload'], maxVisible: 3 },
    },
    features: { objectsPanel: false },
  },

  minimal: {
    gallery: {
      header: { align: 'left', showSubtitle: false },
      arrangement: 'grid',
      columns: 4,
      cardAspect: '1/1',
      filters: { visible: false },
      upload: { qr: false },
      width: 'narrow',
    },
    room: {
      desktop: {
        widgets: [
          w('logo', 'panel.header'),
          w('changeRoom', 'panel.header', { display: 'icon' }),
          w('productInfo', 'topbar.start', { fields: ['name'] }),
          w('assistant', 'topbar.end', { display: 'label' }),
          w('order', 'topbar.end', { display: 'label' }),
          w('addProduct', 'canvas.bottomLeft', { display: 'icon' }),
          hidden('appliedProducts', 'topbar.start'),
          hidden('share'), hidden('download'), hidden('compare'), hidden('upload'), hidden('menu'),
          hidden('cart'), hidden('favorites'),
        ],
        panel: { side: 'left', width: 360, style: 'attached', start: 'auto', collapsible: true, tabs: { cart: false, favorites: false }, catalogColumns: 2 },
        topBar: { style: 'floating' },
        surfaceTools: { style: 'floating' },
        canvas: { background: '#FFFFFF' },
      },
      mobile: { actions: ['order', 'share'], maxVisible: 2 },
    },
    features: { measurement: false, render: false },
  },

  showroom: {
    gallery: {
      header: { align: 'center' },
      arrangement: 'grid',
      columns: 3,
      cardAspect: '16/9',
      showNames: true,
      width: 'wide',
    },
    room: {
      desktop: {
        widgets: [
          w('logo', 'topbar.start'),
          w('changeRoom', 'topbar.start', { display: 'icon-label' }),
          w('appliedProducts', 'topbar.center'),
          w('productInfo', 'topbar.center', { fields: ['brand', 'name', 'size', 'color'] }),
          w('cart', 'topbar.end', { display: 'icon-label' }),
          w('order', 'topbar.end', { display: 'icon-label' }),
          w('share', 'canvas.topRight', { display: 'icon' }),
          w('download', 'canvas.topRight', { display: 'icon' }),
          w('compare', 'canvas.topRight', { display: 'icon' }),
          w('upload', 'canvas.topRight', { display: 'icon' }),
          w('addProduct', 'canvas.bottomLeft', { display: 'icon-label' }),
          hidden('menu'),
          hidden('favorites'),
        ],
        panel: { side: 'left', width: 460, style: 'floating', start: 'open', collapsible: false, tabs: { cart: true, favorites: true }, catalogColumns: 2 },
        topBar: { style: 'floating' },
        surfaceTools: { style: 'floating' },
        canvas: { background: '#F3F1EC' },
      },
    },
  },
};

export const PRESET_IDS = Object.keys(LAYOUT_PRESETS);
